import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, BookOpen, Heart, GraduationCap, ChevronRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  advent: '🕯️',
  easter: '✝️',
  summer: '☀️',
  lent: '🙏',
  fall: '🍂',
  'new-year': '🎉',
  thanksgiving: '🦃',
  family: '👨‍👩‍👧‍👦',
  missions: '🌍',
  'youth-camp': '⛺',
  baptism: '💧',
  'sermon-notes': '📝',
  'small-group': '👥',
  custom: '⭐'
};

const CATEGORY_LABELS = {
  advent: 'Advent',
  easter: 'Easter',
  summer: 'Summer',
  lent: 'Lent',
  fall: 'Fall',
  'new-year': 'New Year',
  thanksgiving: 'Thanksgiving',
  family: 'Family',
  missions: 'Missions',
  'youth-camp': 'Youth Camp',
  baptism: 'Baptism',
  'sermon-notes': 'Sermon Notes',
  'small-group': 'Small Group',
  custom: 'My Templates'
};

export default function TemplateLibrary({ onSelectTemplate, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(templates.map(t => t.category))];
  
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="template-library-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Template Library</h2>
              <p className="text-[#475569] mt-1">Pre-built study outlines for common sermon series</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-6 py-4 border-b border-[#E2DFD8] overflow-x-auto">
          <div className="flex gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#1B3A6B] text-white'
                    : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
                }`}
                data-testid={`template-category-${cat}`}
              >
                {cat !== 'all' && <span>{CATEGORY_ICONS[cat]}</span>}
                {cat === 'all' ? 'All Templates' : CATEGORY_LABELS[cat] || cat}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-[#475569]">Loading templates...</div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {filteredTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    selectedTemplate?.id === template.id
                      ? 'border-[#1B3A6B] bg-[#F9F8F6]'
                      : 'border-[#E2DFD8] hover:border-[#B8860B]/50 hover:bg-[#F9F8F6]'
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{CATEGORY_ICONS[template.category]}</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#0F1A2A]">{template.name}</h3>
                      <p className="text-sm text-[#475569] mt-1 line-clamp-2">{template.description}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#475569]">
                        <span className="flex items-center gap-1">
                          {template.content_type === 'bible-study' && <BookOpen className="w-3 h-3" />}
                          {template.content_type === 'devotional' && <Heart className="w-3 h-3" />}
                          {template.content_type === 'curriculum' && <GraduationCap className="w-3 h-3" />}
                          {template.content_type}
                        </span>
                        <span>{template.default_weeks} weeks</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Template Preview */}
        {selectedTemplate && (
          <div className="border-t border-[#E2DFD8] p-6 bg-[#F9F8F6]">
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-[#1B3A6B]">{selectedTemplate.name}</h3>
                <p className="text-sm text-[#475569] mt-1">{selectedTemplate.description}</p>
                
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#475569]">Scriptures:</span>
                    <p className="font-medium text-[#0F1A2A] mt-1">{selectedTemplate.default_scriptures}</p>
                  </div>
                  <div>
                    <span className="text-[#475569]">Focus Areas:</span>
                    <p className="font-medium text-[#0F1A2A] mt-1">{selectedTemplate.focus_areas.join(', ')}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <span className="text-[#475569] text-sm">Outline:</span>
                  <pre className="mt-1 text-sm text-[#0F1A2A] whitespace-pre-wrap font-sans">{selectedTemplate.outline}</pre>
                </div>
              </div>
              
              <button
                onClick={handleUseTemplate}
                className="btn-primary px-6 py-3 rounded-lg font-medium flex items-center gap-2"
                data-testid="use-template-button"
              >
                <Sparkles className="w-5 h-5" />
                Use Template
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
